// Feed de ofertas por loja (Shopee, Mercado Livre, Amazon) buscadas nas APIs
// oficiais pela convert-link (acao offers), com estados de credencial.

import { useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { KeyRound, Search } from "lucide-react";
import { SHOPEE_CATEGORIES } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { callErrorMessage, searchOffers, type FeedResponse, type FeedStore } from "@/lib/hooks/useConvertLink";
import { cn } from "@/lib/utils";
import { OfferCard } from "./OfferCard";

const STORES: ReadonlyArray<{ id: FeedStore; label: string; placeholder: string }> = [
  { id: "shopee", label: "Shopee", placeholder: "Buscar na Shopee (ex.: fone bluetooth)" },
  { id: "mercadolivre", label: "Mercado Livre", placeholder: "" },
  { id: "amazon", label: "Amazon", placeholder: "Buscar na Amazon (ex.: kindle)" },
];

const QUICK_CATEGORIES = SHOPEE_CATEGORIES.slice(0, 8);

function CredentialNotice({ result }: { result: Exclude<FeedResponse, { status: "ok" }> }): JSX.Element {
  const linkToCredentials = result.status !== "unavailable";
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-6 py-12 text-center">
      <KeyRound size={32} className="text-muted-foreground/50" aria-hidden="true" />
      <p className="max-w-md text-sm text-muted-foreground">{result.message}</p>
      {linkToCredentials && (
        <Button asChild size="sm" variant="secondary">
          <Link to="/app/account">Abrir Credenciais</Link>
        </Button>
      )}
    </div>
  );
}

function Results({ store, keyword }: { store: FeedStore; keyword: string }): JSX.Element {
  const { data, isLoading, error } = useQuery<FeedResponse, Error>({
    queryKey: ["offers-feed", store, keyword],
    queryFn: () => searchOffers({ store, keyword, page: 1 }),
    staleTime: 5 * 60_000,
    retry: false,
  });
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-busy="true" aria-label="Carregando ofertas">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-80 animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.4)]" />
        ))}
      </div>
    );
  }
  if (error || !data) {
    return (
      <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {callErrorMessage(error)}
      </p>
    );
  }
  if (data.status !== "ok") {
    return <CredentialNotice result={data} />;
  }
  if (data.offers.length === 0) {
    return <p className="py-12 text-center text-sm text-muted-foreground">Nenhuma oferta encontrada. Tente outra busca.</p>;
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {data.offers.map((offer) => (
        <OfferCard key={offer.itemId} offer={offer} store={store} />
      ))}
    </div>
  );
}

export function OffersFeed(): JSX.Element {
  const [store, setStore] = useState<FeedStore>("shopee");
  const [input, setInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const current = STORES.find((s) => s.id === store)!;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setKeyword(input.trim().slice(0, 100));
  };
  const switchStore = (next: FeedStore): void => {
    setStore(next);
    setInput("");
    setKeyword("");
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Lojas do feed">
        {STORES.map((s) => (
          <button
            key={s.id}
            role="tab"
            aria-selected={store === s.id}
            onClick={() => switchStore(s.id)}
            className={cn(
              "rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              store === s.id ? "border-primary/50 bg-primary/15 text-foreground" : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {s.label}
          </button>
        ))}
      </div>
      {store !== "mercadolivre" && (
        <form onSubmit={submit} className="flex gap-2" role="search">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={current.placeholder} aria-label="Buscar ofertas" maxLength={100} />
          <Button type="submit" variant="secondary">
            <Search size={14} aria-hidden="true" /> Buscar
          </Button>
        </form>
      )}
      {store === "shopee" && (
        <div className="flex flex-wrap gap-2" aria-label="Categorias rápidas">
          {QUICK_CATEGORIES.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => {
                setInput(category.keyword);
                setKeyword(category.keyword);
              }}
              className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            >
              {category.name}
            </button>
          ))}
        </div>
      )}
      <Results store={store} keyword={keyword} />
    </div>
  );
}
